'use client';

/**
 * RfqFinderCard — natural-language RFQ search in mode1's Filter lens.
 *
 * Calls the generic `search` strategy (type=rfq), scoped to the selected
 * project when there is one. Renders hits as rows; clicking a row SELECTS
 * the RFQ (selectNode) so mode1's existing selection-driven canvas paints
 * it (matrix / board / details). It does NOT paint a results canvas — in
 * mode1 the search is a finder, not a results grid.
 *
 * Thin painter over the {hits, facets} envelope: each hit carries its type
 * and data; this card reads the rfq data slice. Surface-agnostic container.
 *
 * @see klyde_intelligence/services/search/ — the strategy
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain explorer
 */

import { useMemo, useState } from 'react';
import { Loader2, Search } from 'lucide-react';
import { useStrategyData } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { useTabStore } from '@/lib/stores/tab-store';
import { useProductLabel } from '@/lib/stores/total-matrix-store';
import { useCardExport } from '@/lib/design/card-export';
import { text } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';

interface RfqData {
  name: string | null;
  state: string | null;
  group: string | null;
  project: string | null;
  project_sid: string | null;
  amount: number | null;
  currency: string | null;
  // set when the RFQ matched via a product it line-items (containment recall)
  via_product?: string | null;
}

interface SearchHit {
  type: string;
  semantic_id: string;
  score: number;
  renderer_card: string;
  data: RfqData;
}

interface SearchResponse {
  hits: SearchHit[];
  total: number;
  returned: number;
  capped: boolean;
  facets: unknown[];
}

export function RfqFinderCard() {
  const strategyName = useCapabilityStore((s) => s.strategyName('search', 'query'));
  const { entityType, entityId } = useViewportSelection();
  const selectNode = useGraphSelectionStore((s) => s.selectNode);
  const openTab = useTabStore((s) => s.openTab);
  const [draft, setDraft] = useState('');
  const [query, setQuery] = useState('');

  const labelTitle = useProductLabel('finder_title');
  const labelScoped = useProductLabel('finder_scoped');
  const labelPlaceholder = useProductLabel('finder_placeholder');
  const labelGo = useProductLabel('search_go');
  const labelUnavailable = useProductLabel('finder_unavailable');
  const labelSearching = useProductLabel('finder_searching');
  const failedTpl = useProductLabel('finder_failed');
  const pickTpl = useProductLabel('finder_pick');
  const viaTpl = useProductLabel('finder_via');

  // project = scope (not type): if a project is selected, narrow to it.
  const scope = useMemo(
    () => (entityType === 'project' && entityId ? { project_id: entityId } : {}),
    [entityType, entityId],
  );
  // A finder, not a results list — fetch a few top matches to jump to.
  const params = useMemo(() => ({ query, types: ['rfq'], scope, limit: 5 }), [query, scope]);
  const enabled = !!strategyName && query.trim().length > 0;
  const { data, isLoading, error } = useStrategyData<SearchResponse>(strategyName, params, enabled);
  const res = data?.data;
  const hits = res?.hits ?? [];

  useCardExport(
    useMemo(() => {
      const status: 'loading' | 'loaded' | 'error' | 'idle' = !enabled
        ? 'idle'
        : isLoading && !res
          ? 'loading'
          : error
            ? 'error'
            : res
              ? 'loaded'
              : 'idle';
      return {
        label: 'Find RFQ',
        content_key: 'rfq_finder',
        status,
        query_key: enabled ? ['strategy-data', strategyName, params] : null,
        text: query ? `rfq: ${query} → ${res?.total ?? 0}` : 'Find RFQ',
        json: res ? { query, total: res.total, scoped: !!scope.project_id } : null,
      };
    }, [enabled, isLoading, error, res, strategyName, params, query, scope]),
  );

  const submit = () => setQuery(draft.trim());
  // Identical to clicking the RFQ in the tree (ChildRow.handleClick):
  // openTab flips the canvas to the RFQ-scoped presets (the canvas reads
  // activeTab.type — without openTab it stays on the project preset);
  // selectNode refreshes the rail, carrying the project as the anchor.
  const pick = (h: SearchHit) => {
    openTab({
      id: `rfq-${h.semantic_id}`,
      type: 'rfq',
      title: h.data.name || 'RFQ',
      entityId: h.semantic_id,
    });
    selectNode(
      {
        id: h.semantic_id,
        label: h.data.name || 'RFQ',
        type: 'rfq',
        status: h.data.state || undefined,
        properties: h.data as unknown as Record<string, unknown>,
      },
      h.data.project_sid || undefined,
    );
  };

  return (
    <div className="overflow-hidden rounded-xl border border-border bg-card">
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <Search className="h-3.5 w-3.5 text-muted-foreground" />
        <span className={text.heading}>{labelTitle}</span>
        {scope.project_id && <span className={cn(text.muted, 'text-[11px]')}>· {labelScoped}</span>}
      </div>
      <div className="space-y-2 p-3">
        <div className="flex gap-1">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && submit()}
            placeholder={labelPlaceholder}
            className="min-w-0 flex-1 rounded border border-border bg-background px-2 py-1 text-xs outline-none focus:border-primary"
          />
          <button
            onClick={submit}
            className="rounded bg-primary px-2 py-1 text-xs text-primary-foreground hover:opacity-90"
          >
            {labelGo}
          </button>
        </div>

        {!strategyName && <div className={text.muted}>{labelUnavailable}</div>}

        {enabled && isLoading && !res && (
          <div className={cn(text.muted, 'flex items-center gap-2')}>
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            {labelSearching}
          </div>
        )}

        {enabled && error && (
          <div className={text.muted}>{failedTpl.replace('{msg}', (error as Error).message)}</div>
        )}

        {res && (
          <>
            <div className={cn(text.muted, 'text-xs')}>
              {pickTpl.replace('{n}', String(res.total))}
            </div>
            <div className="space-y-1">
              {hits.slice(0, 3).map((h) => (
                <button
                  key={h.semantic_id}
                  onClick={() => pick(h)}
                  className="w-full rounded border border-border/50 px-2 py-1 text-left transition-colors hover:border-primary/50"
                >
                  <div className="line-clamp-1 text-xs font-medium">{h.data.name ?? '—'}</div>
                  <div className={cn(text.muted, 'flex flex-wrap gap-x-2 text-[11px]')}>
                    {h.data.state && <span>{h.data.state}</span>}
                    {h.data.project && <span>· {h.data.project}</span>}
                  </div>
                  {h.data.via_product && (
                    <div className={cn(text.muted, 'line-clamp-1 text-[11px] italic opacity-80')}>
                      {viaTpl.replace('{product}', h.data.via_product)}
                    </div>
                  )}
                </button>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
